/**
 * GeoConfigContext.tsx
 * ─────────────────────────────────────────────────────────────
 * Fetches geo-blocking configuration from the backend API at runtime.
 * The config lives in the DB (GeoConfigs table) — update a DB row and
 * all active sessions pick it up within the poll interval (5 minutes),
 * with NO rebuild, NO restart, NO file editing required.
 *
 * Falls back to hardcoded defaults from geoConfig.ts if the API is unreachable.
 * ─────────────────────────────────────────────────────────────
 */

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { BLOCKED_COUNTRY_CODES } from '../geoConfig';

const GEO_CONFIG_URL = `${process.env.REACT_APP_NODE_API_BASE_URL}/config/geo`;

/** How often to re-fetch the config from the backend (ms) */
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export interface GeoConfig {
  /** ISO 3166-1 alpha-2 codes blocked from onboarding */
  blockedCountries: string[];
  /** ISO 3166-1 alpha-2 codes where service is available */
  availableCountries: string[];
  /** ISO 3166-1 alpha-2 codes launching soon */
  comingSoonCountries: string[];
  /** Whether to show the regional disclaimer banner at all */
  showBanner: boolean;
  /** Route prefixes that trigger a hard full-screen block */
  hardBlockRoutes: string[];
}

const FALLBACK_CONFIG: GeoConfig = {
  blockedCountries: BLOCKED_COUNTRY_CODES,
  availableCountries: [],
  comingSoonCountries: [],
  showBanner: true,
  hardBlockRoutes: ['/kyc/'],
};

interface GeoConfigContextValue {
  config: GeoConfig;
  isLoading: boolean;
  /** Manually trigger a fresh fetch (e.g. after an admin update) */
  refetch: () => void;
}

const GeoConfigContext = createContext<GeoConfigContextValue>({
  config: FALLBACK_CONFIG,
  isLoading: true,
  refetch: () => {},
});

export function GeoConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<GeoConfig>(FALLBACK_CONFIG);
  const [isLoading, setIsLoading] = useState(true);

  const fetchConfig = useCallback(async (isInitial = false) => {
    if (isInitial) setIsLoading(true);
    try {
      const res = await fetch(GEO_CONFIG_URL, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      // Backend wraps response in { data: { blockedCountries, showBanner, hardBlockRoutes } }
      const data: Partial<GeoConfig> = json?.data ?? json;

      setConfig({
        blockedCountries: Array.isArray(data.blockedCountries)
          ? data.blockedCountries.map((c: string) => c.toUpperCase())
          : FALLBACK_CONFIG.blockedCountries,
        availableCountries: Array.isArray(data.availableCountries)
          ? data.availableCountries.map((c: string) => c.toUpperCase())
          : FALLBACK_CONFIG.availableCountries,
        comingSoonCountries: Array.isArray(data.comingSoonCountries)
          ? data.comingSoonCountries.map((c: string) => c.toUpperCase())
          : FALLBACK_CONFIG.comingSoonCountries,
        showBanner: typeof data.showBanner === 'boolean'
          ? data.showBanner
          : FALLBACK_CONFIG.showBanner,
        hardBlockRoutes: Array.isArray(data.hardBlockRoutes)
          ? data.hardBlockRoutes
          : FALLBACK_CONFIG.hardBlockRoutes,
      });
    } catch (err) {
      console.warn('[GeoConfig] Could not fetch config from API — using defaults.', err);
      setConfig(FALLBACK_CONFIG);
    } finally {
      if (isInitial) setIsLoading(false);
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchConfig(true);
  }, [fetchConfig]);

  // Periodic background polling — picks up DB changes without page reload
  useEffect(() => {
    const interval = setInterval(() => fetchConfig(false), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchConfig]);

  return (
    <GeoConfigContext.Provider value={{ config, isLoading, refetch: () => fetchConfig(false) }}>
      {children}
    </GeoConfigContext.Provider>
  );
}

/** Hook to read the current runtime geo config anywhere in the app */
export function useGeoConfig(): GeoConfigContextValue {
  return useContext(GeoConfigContext);
}

/** Helper: returns true if the given country code is blocked per the runtime config */
export function useIsCountryBlocked(countryCode: string | null): boolean {
  const { config } = useGeoConfig();
  if (!countryCode) return false;
  return config.blockedCountries.includes(countryCode.toUpperCase());
}
